import { useRef, useState } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ISurgeItemRef } from "@/api_framework/api_modals/SurgePricing";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useSurgePricingAPI } from "@/app/hooks/api_hooks/surge_pricing/useSurgePricingAPI";

interface IItemPickerProps {
  clinicId: string;
  selected: ISurgeItemRef[];
  onChange: (items: ISurgeItemRef[]) => void;
  register: UseFormRegisterReturn;
  single?: boolean;
  disabled?: boolean;
}

const KEY_SEPARATOR = "::";
const SEARCH_DELAY_MS = 350;

// Searches services + BOMs at a clinic (min 3 letters); picked items show as badges.
const ItemPicker = ({ clinicId, selected, onChange, register, single = false, disabled = false }: IItemPickerProps) => {
  const { searchItems } = useSurgePricingAPI();
  // Remounts the menu after each pick so the search box clears.
  const [menuKey, setMenuKey] = useState<number>(0);

  const latestQuery = useRef<string>("");

  // Waits for typing to pause; an empty result leaves the menu's last list untouched.
  const searchOptions = async (query: string) => {
    latestQuery.current = query;
    await new Promise((resolve) => setTimeout(resolve, SEARCH_DELAY_MS));
    if (latestQuery.current !== query) return [];
    return (await searchItems(clinicId, query)).map((item) => ({
      key: `${item.item_type}${KEY_SEPARATOR}${item.item_id}${KEY_SEPARATOR}${item.name}`,
      label: `${item.name} · ${item.item_type === "BOM" ? "BOM" : "Service"}${item.price != null ? ` · ₹${item.price}` : ""}`,
    }));
  };

  const onPick = (key?: string) => {
    if (!key) return;
    const [item_type, item_id, name] = key.split(KEY_SEPARATOR);
    const ref = { item_id, item_type, name } as ISurgeItemRef;
    if (single) onChange([ref]);
    else if (!selected.some((s) => s.item_id === item_id)) onChange([...selected, ref]);
    setMenuKey((k) => k + 1);
  };

  return (
    <div className="flex flex-col gap-2">
      {!disabled && (
        <SearchableSelectMenu
          key={`${clinicId}:${menuKey}`}
          errorMessages={[]}
          fieldError={undefined}
          register={register}
          placeholder={clinicId ? "Search Service or BOM (min 3 letters)" : "Select a clinic first"}
          disabled={!clinicId}
          onSelectItem={(item) => onPick(item?.id)}
          withAPISearch={{ apiCallback: searchOptions, reducerIdKey: "key", reducerTitleKey: "label" }}
          showTooltips={true}
          showTypedErrors={false}
          showDropdownIcon={true}
          listBoxClassName="w-full"
          className="text-gray-800"
          containerClassName="w-full"
        />
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((item) => (
            <SimpleBadge
              key={item.item_id}
              colorClassName="text-[#0068FF] bg-[#EBF5FF] ring-blue-500/10"
              label={
                <span className="flex items-center gap-1">
                  {item.name ?? item.item_id} · {item.item_type === "BOM" ? "BOM" : "Service"}
                  {!disabled && (
                    <XMarkIcon
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => onChange(selected.filter((s) => s.item_id !== item.item_id))}
                    />
                  )}
                </span>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ItemPicker;
